import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAccessToken } from '../utils/authToken';
import { getUserInitials } from '../utils/userInitials';
import { useInsurerLogo } from '../hooks/useInsurerLogo';
import { mfaService, MfaStatus } from '../services/mfaService';
import MfaProfileSection from '../components/Mfa/MfaProfileSection';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Profile: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const isInsurer = user?.role === 'INSURER';
  const { orgName } = useInsurerLogo();

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [privacyMessage, setPrivacyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Insurer specific state
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameMessage, setNameMessage] = useState<string | null>(null);
  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
  const [showMfaModal, setShowMfaModal] = useState(false);

  useEffect(() => {
    if (isInsurer) {
      mfaService.getStatus().then(setMfaStatus).catch(() => {});
    }
  }, [isInsurer, showMfaModal]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/v1/users/me/change-password`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password updated successfully!' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setIsChangingPassword(false);
      } else {
        const errorData = await response.json();
        setMessage({
          type: 'error',
          text: errorData.detail || 'Failed to update password',
        });
      }
    } catch {
      setMessage({
        type: 'error',
        text: 'An error occurred while updating password',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingName(true);
    setNameMessage(null);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/v1/users/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ full_name: nameInput.trim() }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update name');
      }
      const updated = await response.json();
      updateUser({ full_name: updated.full_name });
      setIsEditingName(false);
      setNameMessage('Name updated successfully');
    } catch (err: any) {
      setNameMessage(err.message || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    setPrivacyMessage(null);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/v1/users/me/export`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `flashai-data-export-${new Date().toISOString().split('T')[0]}.json`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      setPrivacyMessage({ type: 'success', text: 'Your data export has been downloaded.' });
    } catch (error) {
      setPrivacyMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to export data',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deletePassword.length < 8) {
      setPrivacyMessage({ type: 'error', text: 'Enter your current password to confirm deletion.' });
      return;
    }
    setIsDeleting(true);
    setPrivacyMessage(null);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE}/v1/users/me`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: deletePassword }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Account deletion failed');
      }
      logout();
      navigate('/');
    } catch (error) {
      setPrivacyMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to delete account',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isPrivilegedAccount =
    user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'INSURER' || user?.is_superuser;

  const formatShortDate = (iso?: string) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ───────────── INSURER PROFILE VIEW (SCREENSHOT 4) ─────────────
  if (isInsurer) {
    const initials = getUserInitials(user?.full_name, user?.email);
    const displayName = user?.full_name || 'Insurer Admin';
    const activeOrgName = orgName || 'Insurance Partner';

    return (
      <div className="min-h-screen bg-[#f8fafc] py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {nameMessage && (
            <div className="p-3 text-sm rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200">
              {nameMessage}
            </div>
          )}

          {/* Card 1: User Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#0c1e40] text-white font-bold text-2xl flex items-center justify-center shrink-0">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0c1e40]">{displayName}</h1>
                <p className="text-sm text-gray-500 mt-0.5">{user?.email}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                    Active
                  </span>
                  <span className="px-3 py-0.5 rounded-full text-xs font-semibold bg-[#0c1e40] text-white">
                    {user?.org_role === 'org_admin' ? `Admin · ${activeOrgName}` : `Member · ${activeOrgName}`}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    Member since {formatShortDate(user?.created_at)}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  setNameInput(user?.full_name || '');
                  setIsEditingName(true);
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
              >
                Edit name
              </button>
            </div>
          </div>

          {/* Card 2: Sign-in & security */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6">
            <h2 className="text-base font-bold text-[#0c1e40]">Sign-in &amp; security</h2>

            {/* Row 1: Password */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-bold text-[#0c1e40]">Password</h3>
                <p className="text-xs text-gray-500 mt-0.5">Use at least 12 characters you don&apos;t use anywhere else.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors shrink-0"
              >
                Change password
              </button>
            </div>

            {isChangingPassword && (
              <form onSubmit={handlePasswordChange} className="p-4 bg-slate-50 border border-gray-200 rounded-xl space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handleInputChange}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  />
                </div>
                {message && (
                  <div className={`p-2.5 rounded-lg text-xs ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {message.text}
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-[#1e293b] disabled:opacity-50"
                  >
                    {isLoading ? 'Updating…' : 'Update Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsChangingPassword(false)}
                    className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="border-t border-gray-100"></div>

            {/* Row 2: Two-factor authentication */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-[#0c1e40]">Two-factor authentication</h3>
                  {mfaStatus?.enabled ? (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded bg-emerald-100 text-emerald-800">
                      Turned on
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 text-amber-800">
                      Not turned on
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Optional for {activeOrgName} — but you&apos;re an admin, so we recommend it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowMfaModal(true)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-sm transition-colors shrink-0 ${
                  mfaStatus?.enabled
                    ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    : 'bg-[#991b1b] text-white hover:bg-[#7f1d1d]'
                }`}
              >
                {mfaStatus?.enabled ? 'Manage 2FA' : 'Turn on 2FA'}
              </button>
            </div>
          </div>

          {/* Card 3: Your data */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-base font-bold text-[#0c1e40] mb-2">Your data</h2>
            <p className="text-xs text-gray-600 leading-relaxed">
              To get a copy of your account data or delete your account, email{' '}
              <a href="mailto:privacy@flashai.com" className="text-blue-600 underline font-medium">
                privacy@flashai.com
              </a>
              . Insurer and admin accounts are handled by our team so your organization&apos;s records stay intact.
            </p>
          </div>
        </div>

        {/* Edit Name Modal */}
        {isEditingName && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <form onSubmit={handleSaveName} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-base font-bold text-[#0c1e40]">Edit your name</h3>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Full name</label>
                <input
                  type="text"
                  required
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditingName(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingName || !nameInput.trim()}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-[#1e293b] disabled:opacity-50"
                >
                  {savingName ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* MFA Setup / Manage Modal */}
        {showMfaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-4">
                <h3 className="text-base font-bold text-[#0c1e40]">Two-factor authentication</h3>
                <button
                  type="button"
                  onClick={() => setShowMfaModal(false)}
                  className="text-gray-400 hover:text-gray-700 font-bold text-lg"
                >
                  &times;
                </button>
              </div>
              <MfaProfileSection />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ───────────── DEFAULT CONSUMER PROFILE VIEW ─────────────
  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                    {user?.full_name || 'Not provided'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                    {user?.email}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Status</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Member Since</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Password Change Section */}
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Security Settings</h2>
            </div>
            <div className="px-6 py-4">
              {!isChangingPassword ? (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Keep your account secure by using a strong password.
                  </p>
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Change Password
                  </button>
                </div>
              ) : (
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {message && (
                    <div
                      className={`p-3 rounded-md ${
                        message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}
                    >
                      {message.text}
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? 'Updating…' : 'Update Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                        });
                        setMessage(null);
                      }}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4">
              <MfaProfileSection />
            </div>
          </div>

          {/* Data & Privacy */}
          <div className="mt-8 bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Data &amp; Privacy</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Download a copy of your account data or permanently delete your account and anonymize personal information.
              </p>

              {privacyMessage && (
                <div
                  className={`p-3 rounded-md ${
                    privacyMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {privacyMessage.text}
                </div>
              )}

              {isPrivilegedAccount ? (
                <p className="text-sm text-gray-600">
                  Admin and insurer accounts must contact{' '}
                  <a href="mailto:privacy@flashai.com" className="text-blue-600 hover:text-blue-800">
                    privacy@flashai.com
                  </a>{' '}
                  for data export or deletion requests.
                </p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleExportData}
                    disabled={isExporting}
                    className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
                  >
                    {isExporting ? 'Preparing export…' : 'Download my data'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteConfirm(true);
                      setDeletePassword('');
                      setPrivacyMessage(null);
                    }}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                  >
                    Delete account
                  </button>
                </div>
              )}

              {showDeleteConfirm && !isPrivilegedAccount && (
                <div className="border border-red-200 bg-red-50 rounded-md p-4 space-y-3">
                  <p className="text-sm text-red-900 font-medium">
                    This permanently anonymizes your account and BCP data. This cannot be undone.
                  </p>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Enter your password to confirm"
                    className="block w-full px-3 py-2 border border-red-200 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  />
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting…' : 'Confirm deletion'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="bg-white text-gray-700 px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Account Overview Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Account Overview</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-gray-300 rounded-full">
                <span className="text-2xl font-semibold text-gray-700">
                  {user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>

              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">{user?.full_name || 'User'}</h3>
                <p className="text-sm text-gray-600">{user?.email}</p>
              </div>

              {user?.is_superuser && (
                <div className="text-center">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                    Administrator
                  </span>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <p>
                    <strong>Joined:</strong>{' '}
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                  <p>
                    <strong>Status:</strong> {user?.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
